import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import {
  LayoutDashboard,
  ClipboardList,
  ShoppingBag,
  Inbox,
  Flame,
  Truck,
  Package,
  Boxes,
  BarChart3,
  Users,
  Store,
  Settings,
  Building2,
  ExternalLink,
  X,
  UtensilsCrossed,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useShop } from "@/contexts/ShopContext";
import { hasPermission } from "@/constants/permissions";
import api from "@/lib/api";

const NAV_SECTIONS = [
  {
    label: "Service Desk",
    items: [
      {
        to: "/dashboard",
        label: "Overview",
        icon: LayoutDashboard,
        perm: "order_status",
      },
      {
        to: "/orders/status",
        label: "Order Status",
        icon: ClipboardList,
        perm: "order_status",
      },
      {
        to: "/orders/new",
        label: "Order Taking",
        icon: ShoppingBag,
        perm: "order_taking",
      },
      {
        to: "/orders/accept",
        label: "Accept Online",
        icon: Inbox,
        perm: "accept_online",
        badgeKey: "acceptOnline",
      },
      {
        to: "/kitchen",
        label: "Kitchen",
        icon: Flame,
        perm: "kitchen",
      },
      {
        to: "/delivery",
        label: "Delivery",
        icon: Truck,
        perm: "delivery",
      },
      {
        to: "/takeaway",
        label: "Take Away",
        icon: Package,
        perm: "takeaway",
      },
    ],
  },
  {
    label: "Management",
    items: [
      {
        to: "/inventory",
        label: "Inventory",
        icon: Boxes,
        perm: "inventory",
      },
      {
        to: "/reports",
        label: "Reports",
        icon: BarChart3,
        perm: "reports",
      },
      {
        to: "/users",
        label: "User Access",
        icon: Users,
        perm: "users",
      },
      {
        to: "/shop-setup",
        label: "Shop Setup",
        icon: Store,
        perm: "shop_setup",
      },
      {
        to: "/settings",
        label: "Settings",
        icon: Settings,
        perm: "settings",
      },
    ],
  },
];

export default function Sidebar({
  mobileOpen = false,
  onCloseMobile,
}) {
  const { user } = useAuth();
  const { currentShopId } = useShop();

  // Sidebar collapse state is handled here.
  const [collapsed, setCollapsed] = useState(false);

  const [acceptOnlineCount, setAcceptOnlineCount] =
    useState(0);

  useEffect(() => {
    if (
      !currentShopId ||
      !hasPermission(user, "accept_online")
    ) {
      return;
    }

    const load = () => {
      api
        .get("/accept-online", {
          params: {
            shop_id: currentShopId,
          },
        })
        .then(({ data }) => {
          setAcceptOnlineCount(
            Array.isArray(data)
              ? data.length
              : 0
          );
        })
        .catch(() => {});
    };

    load();

    const intervalId = setInterval(
      load,
      15000
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [currentShopId, user]);

  const badges = {
    acceptOnline: acceptOnlineCount,
  };

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  return (
    <>
      {/* Mobile backdrop */}

      <div
        className={`sidebar-backdrop ${
          mobileOpen ? "open" : ""
        }`}
        onClick={onCloseMobile}
        data-testid="sidebar-backdrop"
      />

      {/* Sidebar */}

      <aside
        className={`sidebar ${
          collapsed ? "collapsed" : ""
        } ${mobileOpen ? "open" : ""}`}
      >
        {/* =================================================
            BRAND
        ================================================== */}

        <div className="brand">
          <div className="brand-left">
            <div className="brand-mark">
              <UtensilsCrossed size={19} />
            </div>

            <div className="brand-copy">
              <div className="brand-name">
                PENIEL Mission Festival
              </div>

              <div className="brand-sub">
                MISSION FESTIVAL
              </div>
            </div>
          </div>

          {/* Mobile close */}

          <button
            type="button"
            className="sidebar-close"
            onClick={onCloseMobile}
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X size={14} />
          </button>
        </div>

        {/* =================================================
            COLLAPSE BUTTON
        ================================================== */}

        <button
          type="button"
          className="collapse-toggle"
          onClick={toggleSidebar}
          data-testid="sidebar-collapse-btn"
          aria-label={
            collapsed
              ? "Expand sidebar"
              : "Collapse sidebar"
          }
          title={
            collapsed
              ? "Expand sidebar"
              : "Collapse sidebar"
          }
        >
          {collapsed ? (
            <ChevronRight size={15} />
          ) : (
            <ChevronLeft size={15} />
          )}
        </button>

        {/* =================================================
            SUPER ADMIN
        ================================================== */}

        {user && user.role === "superadmin" && (
          <>
            <div className="nav-label">
              Platform
            </div>

            <div className="nav">
              <NavLink
                to="/tenants"
                className={({ isActive }) =>
                  isActive ? "active" : ""
                }
                data-testid="nav-tenants"
                data-tooltip="Tenants"
              >
                <span className="nav-icon">
                  <Building2 size={16} />
                </span>

                <span className="nav-label-text">
                  Tenants
                </span>
              </NavLink>
            </div>
          </>
        )}

        {/* =================================================
            NORMAL USER NAVIGATION
        ================================================== */}

        {user &&
          user.role !== "superadmin" &&
          NAV_SECTIONS.map((section) => {
            const visibleItems =
              section.items.filter((item) =>
                hasPermission(
                  user,
                  item.perm
                )
              );

            if (visibleItems.length === 0) {
              return null;
            }

            return (
              <div
                key={section.label}
                className="nav-section"
              >
                <div className="nav-label">
                  {section.label}
                </div>

                <div className="nav">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;

                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          isActive ? "active" : ""
                        }
                        data-testid={`nav-${item.to
                          .replace(/\//g, "-")
                          .slice(1)}`}
                        data-tooltip={item.label}
                      >
                        <span className="nav-icon">
                          <Icon size={16} />
                        </span>

                        <span className="nav-label-text">
                          {item.label}
                        </span>

                        {item.badgeKey &&
                          badges[
                            item.badgeKey
                          ] > 0 && (
                            <span
                              className="pill-count"
                              data-testid={`badge-${item.badgeKey}`}
                            >
                              {
                                badges[
                                  item.badgeKey
                                ]
                              }
                            </span>
                          )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}

        {/* =================================================
            ONLINE ORDER PREVIEW
        ================================================== */}

        {user &&
          user.role !== "superadmin" && (
            <div
              className="nav online-preview-nav"
              style={{
                marginTop: 26,
              }}
            >
              <a
                href="/order"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-online-order-preview"
                data-tooltip="Online Order Preview"
              >
                <span className="nav-icon">
                  <ExternalLink size={16} />
                </span>

                <span className="nav-label-text">
                  Online Order Preview
                </span>
              </a>
            </div>
          )}

        {/* =================================================
            SYSTEM STATUS
        ================================================== */}

        <div className="system">
          <div className="live">
            <span className="live-dot" />

            <span className="system-text">
              System live
            </span>
          </div>

          <strong className="system-text">
            All stations connected
          </strong>

          <small className="system-text">
            Last sync just now
          </small>
        </div>

        {/* =================================================
            USER PROFILE
        ================================================== */}

        {user && (
          <div className="profile">
            <div className="avatar">
              {user.name?.[0]?.toUpperCase() ||
                "U"}
            </div>

            <div className="profile-text">
              <div className="profile-name">
                {user.name}
              </div>

              <div className="profile-role">
                {user.role.replace(
                  "_",
                  " "
                )}
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}